import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react';
import { getSites, getProjects, saveSites, saveProjects, saveCustomRepos, getCustomRepos, fetchCloudData } from '../utils/storage';
import type { Site, Project, Repository } from '../types';

interface EditModeContextType {
  isEditMode: boolean;
  showPinModal: boolean;
  sites: Site[];
  projects: Project[];
  customRepos: Repository[];
  login: () => void;
  logout: () => void;
  openPinModal: () => void;
  closePinModal: () => void;
  updateSites: (sites: Site[]) => void;
  updateProjects: (projects: Project[]) => void;
  updateCustomRepos: (repos: Repository[]) => void;
}

const EditModeContext = createContext<EditModeContextType | null>(null);

export function useEditMode() {
  const context = useContext(EditModeContext);
  if (!context) {
    throw new Error('useEditMode must be used within EditModeProvider');
  }
  return context;
}

interface EditModeProviderProps {
  children: ReactNode;
}

export function EditModeProvider({ children }: EditModeProviderProps) {
  const [isEditMode, setIsEditMode] = useState(false);
  const [showPinModal, setShowPinModal] = useState(false);
  const [sites, setSites] = useState<Site[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [customRepos, setCustomRepos] = useState<Repository[]>([]);

  // Load data on mount
  useEffect(() => {
    // 1. Initial snapshot instantly
    setSites(getSites());
    setProjects(getProjects());
    setCustomRepos(getCustomRepos());

    // 2. Overwrite silently with real-time cloud stats if available
    fetchCloudData().then(data => {
      if (data) {
        if (data.sites) {
          setSites(data.sites);
          saveSites(data.sites, true); // save locally, but skip loop uploading
        }
        if (data.projects) {
          setProjects(data.projects);
          saveProjects(data.projects, true);
        }
        if (data.customRepos) {
          setCustomRepos(data.customRepos);
          saveCustomRepos(data.customRepos, true);
        }
      }
    });
  }, []);

  const login = useCallback(() => {
    setIsEditMode(true);
    setShowPinModal(false);
  }, []);

  const logout = useCallback(() => {
    setIsEditMode(false);
  }, []);

  const openPinModal = useCallback(() => {
    setShowPinModal(true);
  }, []);

  const closePinModal = useCallback(() => {
    setShowPinModal(false);
  }, []);

  const updateSites = useCallback((newSites: Site[]) => {
    setSites(newSites);
    saveSites(newSites);
  }, []);

  const updateProjects = useCallback((newProjects: Project[]) => {
    setProjects(newProjects);
    saveProjects(newProjects);
  }, []);

  const updateCustomRepos = useCallback((newRepos: Repository[]) => {
    setCustomRepos(newRepos);
    saveCustomRepos(newRepos);
  }, []);

  return (
    <EditModeContext.Provider
      value={{
        isEditMode,
        showPinModal,
        sites,
        projects,
        customRepos,
        login,
        logout,
        openPinModal,
        closePinModal,
        updateSites,
        updateProjects,
        updateCustomRepos,
      }}
    >
      {children}
    </EditModeContext.Provider>
  );
}
